import React, { useEffect, useMemo, useRef, useState } from "react"
import {
    Keyboard,
    Platform,
    ScrollView,
    TextInput,
    TouchableOpacity,
    View,
} from "react-native"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { keepInputFocusOnPress } from "@common/utils/keepInputFocusOnPress"

/**
 * SearchableSelectM — generic single-select dropdown with type-ahead filtering.
 *
 * Visual style mirrors `ChapterSearchDropdown` (focused border, dropdown shadow,
 * row hover) but without the chapter-specific extras (cross-class auto-broaden,
 * AI suggestions, priority chapters, parent grouping). Use this for plain
 * label/value selectors that have enough entries (~10+) to warrant search.
 *
 * Props:
 * - label: Optional label rendered above the input.
 * - options: Array of `{ label, value, leftComponent?, group? }`.
 *   - `leftComponent` renders before the label in the dropdown row + chip
 *     (subject icons).
 *   - `group` (optional string) — when ANY option has it, the dropdown is
 *     grouped by group with a section header per group. Group order matches
 *     the first-seen order in the options array. Useful for class lists
 *     (Primaire / Collège / Lycée).
 * - value: Currently selected value (matches `option.value`).
 * - onSelect: Callback fired with the selected value, or `null` when cleared.
 * - placeholder: Input placeholder text.
 * - disabled: When true, the input is read-only and styled as disabled.
 * - accentInsensitive: Strip accents before substring filtering. Default true.
 * - minSearchToFilter: Minimum query length before filtering. Default 0 — show
 *   all options when the dropdown opens.
 */
const normalize = (str, accentInsensitive) => {
    const lower = (str ?? "").toString().toLowerCase()
    if (!accentInsensitive) return lower
    return lower.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

const renderRow = (option, isSelected, handleSelect, theme) => (
    <TouchableOpacity
        key={`${option?.value}`}
        onPress={() => handleSelect(option)}
        activeOpacity={0.7}
        style={{
            flexDirection: "row",
            alignItems: "center",
            padding: 12,
            backgroundColor: isSelected
                ? theme.colors.primaryLighter
                : theme.colors.white,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.lighter,
        }}
    >
        {option?.leftComponent}
        <View style={{ flex: 1 }}>
            <TextM
                fontSize={14}
                fontWeight={isSelected ? "SemiBold" : "Regular"}
                color={theme.colors.dark}
                numberOfLines={2}
            >
                {option?.label}
            </TextM>
        </View>
        {isSelected && (
            <FontAwesome5
                name="check"
                size={14}
                color={theme.colors.primary}
                style={{ marginLeft: 8 }}
            />
        )}
    </TouchableOpacity>
)

const SearchableSelectM = ({
    label = null,
    options = [],
    value = null,
    onSelect,
    placeholder = "Rechercher...",
    disabled = false,
    accentInsensitive = true,
    minSearchToFilter = 0,
}) => {
    const [searchQuery, setSearchQuery] = useState("")
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef(null)

    const selectedOption = useMemo(() => {
        if (value === null || value === undefined) return null
        return options.find((opt) => opt?.value === value) || null
    }, [options, value])

    const filteredOptions = useMemo(() => {
        const trimmed = searchQuery.trim()
        if (trimmed.length < minSearchToFilter || trimmed.length === 0) {
            return options
        }
        const needle = normalize(trimmed, accentInsensitive)
        return options.filter((opt) =>
            normalize(opt?.label, accentInsensitive).includes(needle)
        )
    }, [options, searchQuery, accentInsensitive, minSearchToFilter])

    const handleSelect = (option) => {
        onSelect?.(option?.value)
        setSearchQuery("")
        setIsFocused(false)
        Keyboard.dismiss()
    }

    // Tapping the selected chip should reopen the dropdown. The input is
    // unmounted while the chip is visible (no input ref → focus() is a no-op),
    // so we drive isFocused directly and let the effect below focus the
    // newly-mounted input on the next render.
    const openDropdown = () => {
        if (disabled) return
        setSearchQuery("")
        setIsFocused(true)
    }

    useEffect(() => {
        if (isFocused) {
            // Schedule on next tick so the input has time to mount.
            const id = setTimeout(() => inputRef.current?.focus(), 0)
            return () => clearTimeout(id)
        }
    }, [isFocused])

    const handleFocus = () => {
        setIsFocused(true)
        setSearchQuery("")
    }

    const handleBlur = () => {
        // Delay so a tap on a dropdown item registers before we collapse.
        setTimeout(() => {
            setIsFocused(false)
        }, 200)
    }

    const clearSelection = () => {
        onSelect?.(null)
        setSearchQuery("")
        inputRef.current?.focus()
    }

    const showDropdown = isFocused && !disabled && options.length > 0

    return (
        <View style={{ zIndex: 10000, position: "relative" }}>
            {label && (
                <TextM
                    color={theme.colors.primary}
                    fontWeight="Medium"
                    fontSize={12}
                    style={{ marginBottom: 5 }}
                >
                    {label}
                </TextM>
            )}

            {/* Selected chip (when an option is picked and not focused) */}
            {selectedOption && !isFocused && (
                <TouchableOpacity
                    onPress={openDropdown}
                    activeOpacity={disabled ? 1 : 0.8}
                    accessibilityRole="button"
                    accessibilityLabel={`${selectedOption.label}, toucher pour changer`}
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: theme.colors.primary,
                        borderRadius: 10,
                        paddingVertical: 12,
                        paddingHorizontal: 12,
                        backgroundColor: theme.colors.primaryLighter,
                    }}
                >
                    {selectedOption.leftComponent}
                    <View style={{ flex: 1 }}>
                        <TextM
                            fontSize={14}
                            fontWeight="SemiBold"
                            color={theme.colors.primary}
                            numberOfLines={1}
                        >
                            {selectedOption.label}
                        </TextM>
                    </View>
                    {!disabled && (
                        <TouchableOpacity
                            onPress={(e) => {
                                // Prevent the parent chip's onPress (which
                                // refocuses the input + reopens the dropdown)
                                // from firing when the user clicks ✕ on web.
                                e?.stopPropagation?.()
                                clearSelection()
                            }}
                            accessibilityRole="button"
                            accessibilityLabel="Désélectionner"
                            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 14,
                                backgroundColor: theme.colors.primary,
                                alignItems: "center",
                                justifyContent: "center",
                                marginLeft: 8,
                            }}
                        >
                            <FontAwesome5
                                name="times"
                                size={14}
                                color={theme.colors.white}
                            />
                        </TouchableOpacity>
                    )}
                </TouchableOpacity>
            )}

            {/* Search input (visible when empty or focused) */}
            {(!selectedOption || isFocused) && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: isFocused
                            ? theme.colors.primary
                            : theme.colors.lightMore,
                        borderRadius: 10,
                        paddingHorizontal: 12,
                        backgroundColor: disabled
                            ? theme.colors.lighter
                            : theme.colors.white,
                    }}
                >
                    <FontAwesome5
                        name="search"
                        size={14}
                        color={theme.colors.grey}
                    />
                    <TextInput
                        ref={inputRef}
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        placeholderTextColor={theme.colors.grey}
                        editable={!disabled}
                        style={{
                            flex: 1,
                            paddingVertical: 12,
                            paddingHorizontal: 10,
                            fontSize: 14,
                            fontFamily: "Montserrat",
                            color: theme.colors.dark,
                        }}
                    />
                </View>
            )}

            {/* Dropdown */}
            {showDropdown && (
                <View
                    {...keepInputFocusOnPress}
                    style={{
                        position: "absolute",
                        top: label ? 75 : 50,
                        left: 0,
                        right: 0,
                        maxHeight: 300,
                        backgroundColor: theme.colors.white,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        overflow: "hidden",
                        ...Platform.select({
                            web: {
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                            },
                            default: {
                                shadowColor: "#000",
                                shadowOffset: { width: 0, height: 4 },
                                shadowOpacity: 0.15,
                                shadowRadius: 12,
                                elevation: 8,
                            },
                        }),
                        zIndex: 10001,
                    }}
                >
                    <ScrollView
                        // "always": taps on non-touchable areas inside the
                        // dropdown (group headers, padding between sections)
                        // don't blur the input and close the dropdown. With
                        // "handled", an imprecise tap on a section header was
                        // killing the dropdown without registering a choice.
                        keyboardShouldPersistTaps="always"
                        nestedScrollEnabled
                    >
                        {filteredOptions.length === 0 ? (
                            <View style={{ padding: 20, alignItems: "center" }}>
                                <TextM
                                    fontSize={13}
                                    color={theme.colors.grey}
                                    style={{ textAlign: "center" }}
                                >
                                    {searchQuery.trim()
                                        ? `Aucun résultat pour "${searchQuery.trim()}"`
                                        : "Aucune option disponible"}
                                </TextM>
                            </View>
                        ) : (
                            (() => {
                                // Render grouped if any option has a `group`
                                // field. Group order = first-seen order. Within
                                // each group, options keep their original
                                // ordering (already filtered).
                                const hasGroups = filteredOptions.some(
                                    (o) => !!o?.group,
                                )
                                if (!hasGroups) {
                                    return filteredOptions.map((option) =>
                                        renderRow(
                                            option,
                                            selectedOption?.value === option?.value,
                                            handleSelect,
                                            theme,
                                        ),
                                    )
                                }
                                const grouped = []
                                const seen = {}
                                for (const opt of filteredOptions) {
                                    const g = opt?.group ?? "—"
                                    if (seen[g] === undefined) {
                                        seen[g] = grouped.length
                                        grouped.push({ group: g, items: [] })
                                    }
                                    grouped[seen[g]].items.push(opt)
                                }
                                return grouped.map(({ group, items }) => (
                                    <View key={`grp-${group}`}>
                                        <View
                                            style={{
                                                backgroundColor:
                                                    theme.colors.lighter,
                                                paddingHorizontal: 12,
                                                paddingVertical: 6,
                                                borderBottomWidth: 1,
                                                borderBottomColor:
                                                    theme.colors.lightMore,
                                            }}
                                        >
                                            <TextM
                                                fontSize={11}
                                                fontWeight="SemiBold"
                                                color={theme.colors.grey}
                                            >
                                                {group}
                                            </TextM>
                                        </View>
                                        {items.map((option) =>
                                            renderRow(
                                                option,
                                                selectedOption?.value ===
                                                    option?.value,
                                                handleSelect,
                                                theme,
                                            ),
                                        )}
                                    </View>
                                ))
                            })()
                        )}
                    </ScrollView>
                </View>
            )}
        </View>
    )
}

export default SearchableSelectM
